import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Plus, Users, Calendar, MapPin, Loader2,
  ChevronLeft, Monitor, Building2,
  Laptop, Check, Search, Sparkles
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { broadcastMatrixUpdate } from '@/hooks/useMatrixBroadcast';

const QuoteModalV2 = ({ isOpen, onClose, selectedCell, onQuote }) => {
  const { projectId } = useParams();
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [trainingOptions, setTrainingOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [addingToQuote, setAddingToQuote] = useState(null);
  const [courseSearch, setCourseSearch] = useState('');
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [delegateSearch, setDelegateSearch] = useState('');

  const [formData, setFormData] = useState({
    courseId: '',
    customCourse: '',
    dateType: 'calendar',
    selectedDate: '',
    selectedMonth: '',
    selectedYear: new Date().getFullYear(),
    trainingType: 'onsite',
    trainingLocation: '',
    selectedDelegates: [],
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchData();
      // Auto-select member from matrix if available
      if (selectedCell?.member?.id) {
        setFormData(prev => ({
          ...prev,
          selectedDelegates: [selectedCell.member.id]
        }));
      }
      // Auto-fill course name from requirement if available
      if (selectedCell?.requirement?.name) {
        setCourseSearch(selectedCell.requirement.name);
      }
    } else {
      // Reset form when modal closes
      setCourseSearch('');
      setFormData({
        courseId: '',
        customCourse: '',
        dateType: 'calendar',
        selectedDate: '',
        selectedMonth: '',
        selectedYear: new Date().getFullYear(),
        trainingType: 'onsite',
        trainingLocation: '',
        selectedDelegates: [],
        notes: ''
      });
      setShowCreateForm(false);
    }
  }, [isOpen, projectId, selectedCell]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch courses
      console.log('Fetching courses...');
      const { data: courses, error: coursesError } = await supabase
        .from('courses')
        .select('*')
        .order('name');

      if (coursesError) {
        console.error('Error fetching courses:', coursesError);
        // Set some default courses if the table doesn't exist
        setAvailableCourses([
          { id: '1', name: 'First Aid Training' },
          { id: '2', name: 'Fire Safety Training' },
          { id: '3', name: 'Manual Handling' },
          { id: '4', name: 'Working at Heights' },
          { id: '5', name: 'Health & Safety Awareness' }
        ]);
      } else {
        const uniqueCourses = courses ? [...new Map(courses.map(item => [item.name, item])).values()] : [];
        console.log('Courses loaded:', uniqueCourses.length);
        setAvailableCourses(uniqueCourses);
      }

      // Fetch team members using projectId from URL
      if (projectId) {
        console.log('Fetching team members for project:', projectId);
        const { data: members, error: membersError } = await supabase
          .from('space_members')
          .select(`
            *,
            user_profiles!user_id (
              id,
              first_name,
              last_name,
              email,
              avatar_url,
              job_title
            )
          `)
          .eq('project_id', projectId);

        if (membersError) {
          console.error('Error fetching team members:', membersError);
        } else {
          const formattedMembers = (members || []).map(sm => ({
            id: sm.user_id,
            name: `${sm.user_profiles.first_name || ''} ${sm.user_profiles.last_name || ''}`.trim() || sm.user_profiles.email,
            email: sm.user_profiles.email,
            role: sm.user_profiles.job_title,
            department: sm.department,
            status: sm.status,
            avatar_url: sm.user_profiles.avatar_url,
            user_id: sm.user_id
          })).sort((a, b) => a.name.localeCompare(b.name));

          console.log('Team members loaded:', formattedMembers.length);
          setTeamMembers(formattedMembers);
        }
      }

      // Fetch real quotes from database for this course
      // If projectId exists, filter by project. Otherwise, fetch across all projects for this user's company
      if (selectedCell?.requirement?.name) {
        console.log('Fetching existing quotes for:', selectedCell.requirement.name, projectId ? `in project: ${projectId}` : 'across all company projects');

        // Build the query
        let query = supabase
          .from('training_quotes')
          .select('*')
          .ilike('course_name', `%${selectedCell.requirement.name}%`)
          .order('created_date', { ascending: false })
          .limit(10);

        // Filter by projectId if in project context, otherwise by company_id
        if (projectId) {
          query = query.eq('project_id', projectId);
        } else if (userProfile?.company_id) {
          query = query.eq('company_id', userProfile.company_id);
        }

        const { data: existingQuotes, error: quotesError } = await query;

        console.log('Filtered quotes error:', quotesError);
        console.log('Filtered quotes data:', existingQuotes);

        if (!quotesError && existingQuotes && existingQuotes.length > 0) {
          console.log('Existing quotes found:', existingQuotes.length, 'quotes');
          // Transform quotes to match the expected format
          const transformedQuotes = existingQuotes.map(quote => ({
            id: quote.id,
            name: quote.course_name || selectedCell?.requirement?.name,
            date: quote.date_prefs || 'Flexible dates',
            delegates: quote.delegates || 1,
            location: quote.locations || 'TBD',
            price: quote.cost ? parseFloat(quote.cost) : null,
            externalId: quote.external_quote_id,
            created: quote.created_date,
            projectId: quote.project_id
          }));
          setTrainingOptions(transformedQuotes);
        } else {
          console.log('No existing quotes found or error:', quotesError);
          setTrainingOptions([]);
        }
      } else {
        setTrainingOptions([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToQuote = async (option) => {
    setAddingToQuote(option.id);
    try {
      // Get the current delegate name from the matrix selection
      const currentDelegate = selectedCell?.member?.name;

      if (!currentDelegate) {
        toast.error('No delegate selected');
        setAddingToQuote(null);
        return;
      }

      console.log('Adding delegate to quote:', option.id, 'Delegate:', currentDelegate);

      // Fetch the current quote to get existing data
      const { data: currentQuote, error: fetchError } = await supabase
        .from('training_quotes')
        .select('*')
        .eq('id', option.id)
        .single();

      if (fetchError || !currentQuote) {
        console.error('Error fetching quote:', fetchError);
        toast.error('Failed to fetch quote details');
        setAddingToQuote(null);
        return;
      }

      // Update the quote with increased delegate count
      const updatedDelegates = (currentQuote.delegates || 1) + 1;

      // Get existing delegate names and add the new one
      const existingDelegateNames = currentQuote.delegate_names || [];
      const updatedDelegateNames = [...existingDelegateNames, currentDelegate];

      // Update the quote in the database
      const { error: updateError } = await supabase
        .from('training_quotes')
        .update({
          delegates: updatedDelegates,
          delegate_names: updatedDelegateNames,
          updated_date: new Date().toISOString()
        })
        .eq('id', option.id);

      if (updateError) {
        console.error('Error updating quote:', updateError);
        toast.error('Failed to update quote');
      } else {
        toast.success(`Added ${currentDelegate} to quote #${option.externalId || option.id}`);

        // Update or create the assignment and link it to this quote
        console.log('Checking selectedCell:', {
          hasSelectedCell: !!selectedCell,
          hasMember: !!selectedCell?.member,
          hasMemberId: !!selectedCell?.member?.id,
          hasRequirement: !!selectedCell?.requirement,
          hasRequirementId: !!selectedCell?.requirement?.id,
          selectedCell: selectedCell
        });

        if (selectedCell?.member?.id && selectedCell?.requirement?.id) {
          // Check if assignment already exists
          const { data: existingAssignment } = await supabase
            .from('compliance_assignments')
            .select('id')
            .eq('team_member_id', selectedCell.member.id)
            .eq('requirement_id', selectedCell.requirement.id)
            .eq('project_id', currentQuote.project_id)
            .single();

          if (existingAssignment) {
            // Update existing assignment (just linking to quote)
            const { error: assignmentError } = await supabase
              .from('compliance_assignments')
              .update({
                assigned: true,
                quote_id: option.id // Link this assignment to the quote
              })
              .eq('id', existingAssignment.id);

            if (assignmentError) {
              console.error('Error updating assignment:', assignmentError);
            } else {
              console.log('Linked existing assignment to quote:', option.id);
            }
          } else {
            // Create new assignment and link to quote (tracking flag only)
            const { error: assignmentError } = await supabase
              .from('compliance_assignments')
              .insert({
                team_member_id: selectedCell.member.id,
                requirement_id: selectedCell.requirement.id,
                project_id: currentQuote.project_id,
                assigned: true,
                quote_id: option.id // Link this assignment to the quote
              });

            if (assignmentError) {
              console.error('Error creating assignment:', assignmentError);
            } else {
              console.log('Created new assignment and linked to quote:', option.id);
            }
          }

          // Also update user_qualifications table if this is from a user's profile
          // member.id is the user_id (compliance_assignments stores user_ids, not space_member_ids)
          const userId = selectedCell.member.id;
          console.log('Using user_id from member:', userId, 'for requirement:', selectedCell.requirement.id);

          if (userId) {
            // Check if user_qualification exists
            // IMPORTANT: For cross-space matching, check by course_link first (if available), then requirement_id
            let existingUserQual = null;

            if (selectedCell.requirement.course_link_id && selectedCell.requirement.course_link_type) {
              // Try to find by course_link (cross-space)
              const { data: qualByCourseLink } = await supabase
                .from('user_qualifications')
                .select('id, status')
                .eq('user_id', userId)
                .eq('course_link_id', selectedCell.requirement.course_link_id)
                .eq('course_link_type', selectedCell.requirement.course_link_type)
                .maybeSingle();

              existingUserQual = qualByCourseLink;
            }

            if (!existingUserQual) {
              // Fallback: try to find by requirement_id (same space)
              const { data: qualByRequirement } = await supabase
                .from('user_qualifications')
                .select('id, status')
                .eq('user_id', userId)
                .eq('requirement_id', selectedCell.requirement.id)
                .maybeSingle();

              existingUserQual = qualByRequirement;
            }

            if (existingUserQual) {
              console.log('Updating existing user_qualification:', existingUserQual.id, 'setting renewal_status to quoting');
              // Update existing user_qualification renewal_status
              const { error: userQualError } = await supabase
                .from('user_qualifications')
                .update({
                  renewal_status: 'quoting',
                  updated_date: new Date().toISOString()
                })
                .eq('id', existingUserQual.id);

              if (userQualError) {
                console.error('Error updating user_qualification:', userQualError);
                toast.error('Failed to update qualification renewal status');
              } else {
                console.log('✅ Successfully updated user_qualification renewal_status to quoting');
              }
            } else {
              console.log('Creating new user_qualification for user:', userId, 'requirement:', selectedCell.requirement.id);
              // Create new user_qualification with renewal_status (status NULL = missing)
              // IMPORTANT: Include course_link fields for cross-space qualification matching
              const { error: userQualError } = await supabase
                .from('user_qualifications')
                .insert({
                  user_id: userId,
                  requirement_id: selectedCell.requirement.id,
                  course_link_id: selectedCell.requirement.course_link_id,
                  course_link_type: selectedCell.requirement.course_link_type,
                  status: null, // NULL = missing (no evidence yet)
                  renewal_status: 'quoting',
                  created_date: new Date().toISOString(),
                  updated_date: new Date().toISOString()
                });

              if (userQualError) {
                console.error('Error creating user_qualification:', userQualError);
                toast.error('Failed to create qualification record');
              } else {
                console.log('✅ Successfully created new user_qualification with renewal_status quoting');
              }
            }
          } else {
            console.warn('No user_id found for team member:', selectedCell.member.id);
          }
        }

        // Show success message
        toast.success('Added to quote successfully');

        // Broadcast matrix update to all connected clients
        broadcastMatrixUpdate(projectId, { action: 'quote_added' });

        // Close modal and trigger refresh immediately (no setTimeout delay)
        onClose();
        if (onQuote) {
          onQuote({
            success: true,
            quoteId: option.id,
            assignmentId: selectedCell?.assignment?.id
          });
        }
      }
    } catch (error) {
      console.error('Error in handleAddToQuote:', error);
      toast.error('Failed to add to quote');
    } finally {
      setAddingToQuote(null);
    }
  };

  const handleCreateQuote = async () => {
    if (!courseSearch.trim()) {
      toast.error('Please enter a course name');
      return;
    }

    if (formData.selectedDelegates.length === 0) {
      toast.error('Please select at least one delegate');
      return;
    }

    if (formData.dateType === 'calendar' && !formData.selectedDate) {
      toast.error('Please select a date');
      return;
    }

    if (formData.dateType === 'month' && !formData.selectedMonth) {
      toast.error('Please select a month');
      return;
    }

    setCreatingQuote(true);
    try {
      // Get selected delegate names
      const selectedDelegateNames = formData.selectedDelegates
        .map(id => teamMembers.find(m => m.id === id)?.name)
        .filter(Boolean);

      // Format the month correctly - YYYY-MM format for database
      let formattedMonth = null;
      if (formData.dateType === 'month' && formData.selectedMonth) {
        formattedMonth = `${formData.selectedYear}-${formData.selectedMonth}`;
      }

      // Prepare the data - keep ALL fields for webhook compatibility
      const quoteData = {
        // Standard booking fields (same as BookingModal)
        datePreference: formData.dateType === 'calendar' ? 'specific' : 'month',
        preferredDate: formData.dateType === 'calendar' ? formData.selectedDate : null,
        preferredMonth: formData.dateType === 'month' ? formattedMonth : null,
        numberOfAttendees: formData.selectedDelegates.length.toString(), // Convert to string like BookingModal
        location: formData.trainingLocation || formData.trainingType,
        notes: formData.notes,
        // IDs from matrix (if available)
        requirementId: selectedCell?.requirement?.id || undefined,
        memberId: selectedCell?.member?.id || undefined,
        // Additional data for quotes (that webhook might need)
        courseName: courseSearch,
        delegates: selectedDelegateNames,
        trainingType: formData.trainingType
      };

      // Call the parent's onQuote callback which handles the saving
      if (onQuote) {
        await onQuote(quoteData);
      }

      // Reset form
      setFormData({
        courseId: '',
        customCourse: '',
        dateType: 'calendar',
        selectedDate: '',
        selectedMonth: '',
        selectedYear: new Date().getFullYear(),
        trainingType: 'onsite',
        trainingLocation: '',
        selectedDelegates: [],
        notes: ''
      });
      setCourseSearch('');
      setShowCreateForm(false);

      toast.success('Quote requested successfully!');

      // Broadcast matrix update to all connected clients
      broadcastMatrixUpdate(projectId, { action: 'quote_created' });

      onClose();
    } catch (error) {
      console.error('Error creating quote:', error);
      toast.error('Failed to create quote');
    } finally {
      setCreatingQuote(false);
    }
  };

  const handleStartNewQuote = () => {
    // Log selectedCell data to verify course link fields are present
    console.log('Starting new quote with selectedCell:', {
      selectedCell,
      requirement: selectedCell?.requirement,
      course_link_id: selectedCell?.requirement?.course_link_id,
      course_link_type: selectedCell?.requirement?.course_link_type
    });

    onClose();

    // Build URL with search params if we have selectedCell data
    const params = new URLSearchParams();

    if (selectedCell?.requirement?.name) {
      params.set('course', selectedCell.requirement.name);
    }

    // Pass course link data if available (from course_link_id and course_link_type columns)
    if (selectedCell?.requirement?.course_link_id && selectedCell?.requirement?.course_link_type) {
      console.log('Found course link, adding to URL params:', {
        id: selectedCell.requirement.course_link_id,
        type: selectedCell.requirement.course_link_type
      });
      params.set('courseType', selectedCell.requirement.course_link_type);
      params.set('courseId', selectedCell.requirement.course_link_id);
    } else {
      console.log('No course link found in requirement - will use name matching');
    }

    if (selectedCell?.member?.id) {
      params.set('delegate', selectedCell.member.id);
    }

    // Add the current location as 'from' param so we can return here
    params.set('from', location.pathname);

    const queryString = params.toString();
    const url = `/space/${projectId}/build-quote${queryString ? `?${queryString}` : ''}`;

    navigate(url);
  };

  const filteredCourses = courseSearch.trim()
    ? availableCourses.filter(course =>
        course.name.toLowerCase().includes(courseSearch.toLowerCase())
      )
    : availableCourses.slice(0, 5); // Show top 5 when no search

  const filteredDelegates = delegateSearch.trim()
    ? teamMembers.filter(member =>
        member.name.toLowerCase().includes(delegateSearch.toLowerCase())
      )
    : teamMembers;


  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {showCreateForm ? 'Create Training Quote' : 'Available Training'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {showCreateForm
                  ? 'Configure your training requirements'
                  : 'Select training options to add to your quote'
                }
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
            <AnimatePresence mode="wait">
              {!showCreateForm ? (
                <motion.div
                  key="options"
                  initial={{ opacity: 0, x: 0 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Start New Quote Button - RESTORED FROM ORIGINAL */}
                  <div className="flex justify-center mb-6">
                    <button
                      onClick={handleStartNewQuote}
                      data-action="quick-quote"
                      className="px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium"
                    >
                      <Plus className="h-5 w-5" />
                      Start New Quote
                    </button>
                  </div>

                  {/* Training Options - RESTORED FROM ORIGINAL */}
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {trainingOptions.map((option) => (
                        <motion.div
                          key={option.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow bg-white"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {option.name}
                                </h3>
                                {option.externalId && (
                                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                                    Quote #{option.externalId}
                                  </span>
                                )}
                              </div>

                              <div className="mt-3 space-y-2">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Calendar className="h-4 w-4 text-gray-400" />
                                  <span>{option.date}</span>
                                </div>

                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Users className="h-4 w-4 text-gray-400" />
                                  <span>{option.delegates} {option.delegates === 1 ? 'Delegate' : 'Delegates'}</span>
                                </div>

                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <MapPin className="h-4 w-4 text-gray-400" />
                                  <span>{option.location}</span>
                                </div>

                                {option.created && (
                                  <div className="text-xs text-gray-500 mt-2">
                                    Created {new Date(option.created).toLocaleDateString('en-GB')}
                                  </div>
                                )}
                              </div>
                            </div>

                            <button
                              onClick={() => handleAddToQuote(option)}
                              disabled={addingToQuote === option.id}
                              className="ml-4 px-5 py-2.5 bg-white border-2 border-gray-900 text-gray-900 rounded-full hover:bg-gray-900 hover:text-white transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {addingToQuote === option.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Plus className="h-4 w-4" />
                              )}
                              Add to quote
                            </button>
                          </div>

                          {option.price && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <span className="text-sm text-gray-500">Estimated price:</span>
                              <span className="text-lg font-semibold text-gray-900 ml-2">
                                £{option.price}
                              </span>
                            </div>
                          )}
                        </motion.div>
                      ))}

                      {trainingOptions.length === 0 && (
                        <div className="text-center py-12">
                          <p className="text-gray-500">No training options available at the moment.</p>
                          <button
                            onClick={handleStartNewQuote}
                            className="mt-4 text-blue-600 hover:text-blue-700 font-medium text-sm"
                          >
                            Request custom training quote
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                >
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="mb-6 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 group"
                  >
                    <ChevronLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                    Back to options
                  </button>

                  <div className="space-y-6">
                    {/* Course Selection with Search */}
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-3 block">
                        Training Course
                      </label>
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            value={courseSearch}
                            onChange={(e) => {
                              setCourseSearch(e.target.value);
                              setShowCourseDropdown(true);
                            }}
                            onFocus={() => setShowCourseDropdown(true)}
                            placeholder="Search courses or type custom..."
                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        {/* Course Dropdown */}
                        {showCourseDropdown && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto"
                            onMouseLeave={() => setTimeout(() => setShowCourseDropdown(false), 300)}
                          >
                            {filteredCourses.length > 0 ? (
                              <>
                                {!courseSearch.trim() && (
                                  <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                                    Popular courses
                                  </div>
                                )}
                                {filteredCourses.map(course => (
                                  <button
                                    key={course.id}
                                    onClick={() => {
                                      setCourseSearch(course.name);
                                      setFormData(prev => ({ ...prev, courseId: course.id }));
                                      setShowCourseDropdown(false);
                                    }}
                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                                  >
                                    <div className="font-medium text-gray-900">{course.name}</div>
                                    {course.description && (
                                      <div className="text-sm text-gray-500 mt-0.5">{course.description}</div>
                                    )}
                                  </button>
                                ))}
                              </>
                            ) : courseSearch.trim() ? (
                              <div className="px-4 py-8 text-center">
                                <p className="text-gray-500 text-sm mb-2">No matching courses found</p>
                                <p className="text-gray-900 font-medium">Using: "{courseSearch}" as custom course</p>
                              </div>
                            ) : null}
                          </motion.div>
                        )}
                      </div>
                    </div>

                    {/* Date Selection */}
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-3 block">
                        Preferred Date
                      </label>
                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={() => setFormData(prev => ({ ...prev, dateType: 'calendar' }))}
                          className={cn(
                            "flex-1 py-2.5 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2",
                            formData.dateType === 'calendar'
                              ? "bg-gray-900 text-white"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          )}
                        >
                          <Calendar className="h-4 w-4" />
                          Specific Date
                        </button>
                        <button
                          onClick={() => setFormData(prev => ({ ...prev, dateType: 'month' }))}
                          className={cn(
                            "flex-1 py-2.5 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2",
                            formData.dateType === 'month'
                              ? "bg-gray-900 text-white"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          )}
                        >
                          <Calendar className="h-4 w-4" />
                          Month
                        </button>
                      </div>
                      {formData.dateType === 'calendar' ? (
                        <input
                          type="date"
                          value={formData.selectedDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, selectedDate: e.target.value }))}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <select
                            value={formData.selectedMonth}
                            onChange={(e) => setFormData(prev => ({ ...prev, selectedMonth: e.target.value }))}
                            className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select month...</option>
                            {months.map((month, i) => (
                              <option key={month} value={String(i + 1).padStart(2, '0')}>
                                {month}
                              </option>
                            ))}
                          </select>
                          <select
                            value={formData.selectedYear}
                            onChange={(e) => setFormData(prev => ({ ...prev, selectedYear: parseInt(e.target.value) }))}
                            className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            {[0, 1, 2].map(offset => {
                              const year = new Date().getFullYear() + offset;
                              return <option key={year} value={year}>{year}</option>;
                            })}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Training Type */}
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-3 block">
                        Training Type
                      </label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { value: 'onsite', label: 'Onsite', icon: MapPin },
                          { value: 'classroom', label: 'Classroom', icon: Building2 },
                          { value: 'remote', label: 'Remote', icon: Monitor },
                          { value: 'elearning', label: 'E-Learning', icon: Laptop }
                        ].map(type => (
                          <button
                            key={type.value}
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              trainingType: type.value,
                              // Clear location if switching to remote/elearning
                              trainingLocation: (type.value === 'remote' || type.value === 'elearning') ? '' : prev.trainingLocation
                            }))}
                            className={cn(
                              "py-2.5 px-3 rounded-xl border-2 transition-colors flex flex-col items-center gap-1.5",
                              formData.trainingType === type.value
                                ? "border-blue-500 bg-blue-50 text-blue-700"
                                : "border-gray-200 hover:bg-gray-50"
                            )}
                          >
                            <type.icon className="h-4 w-4" />
                            <span className="text-xs font-medium">{type.label}</span>
                          </button>
                        ))}
                      </div>

                      {/* Conditional Location Field */}
                      {(formData.trainingType === 'onsite' || formData.trainingType === 'classroom') && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="mt-3"
                        >
                          <label className="text-xs font-medium text-gray-600 mb-1.5 block flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5" />
                            Training Location
                          </label>
                          <input
                            type="text"
                            value={formData.trainingLocation}
                            onChange={(e) => setFormData(prev => ({ ...prev, trainingLocation: e.target.value }))}
                            placeholder="Enter address or location details..."
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </motion.div>
                      )}
                    </div>

                    {/* Delegates */}
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-3 block">
                        Select Delegates ({formData.selectedDelegates.length} selected)
                      </label>

                      {teamMembers.length > 0 ? (
                        <div className="space-y-3">
                          {/* Search Bar */}
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              value={delegateSearch}
                              onChange={(e) => setDelegateSearch(e.target.value)}
                              placeholder="Search team members..."
                              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                          </div>

                          {/* Delegates List */}
                          <div className="border border-gray-200 rounded-xl max-h-48 overflow-y-auto">
                            {filteredDelegates.length > 0 ? (
                              <div className="divide-y divide-gray-100">
                                {filteredDelegates.map(member => {
                                  const isSelected = formData.selectedDelegates.includes(member.id);
                                  return (
                                    <label
                                      key={member.id}
                                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {
                                          setFormData(prev => ({
                                            ...prev,
                                            selectedDelegates: isSelected
                                              ? prev.selectedDelegates.filter(id => id !== member.id)
                                              : [...prev.selectedDelegates, member.id]
                                          }));
                                        }}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                      />
                                      <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900">
                                          {member.name}
                                          {selectedCell?.member?.id === member.id && (
                                            <span className="ml-2 text-xs text-blue-600">(From Matrix)</span>
                                          )}
                                        </div>
                                        {member.role && (
                                          <div className="text-xs text-gray-500">{member.role}</div>
                                        )}
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="px-4 py-8 text-center">
                                <p className="text-sm text-gray-500">No team members found</p>
                              </div>
                            )}
                          </div>

                          {/* Selected Delegates Summary */}
                          {formData.selectedDelegates.length > 0 && (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600 font-medium">
                                  {formData.selectedDelegates.length} delegate{formData.selectedDelegates.length !== 1 ? 's' : ''} selected
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, selectedDelegates: [] }))}
                                  className="text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                  Clear all
                                </button>
                              </div>

                              {/* Selected Names */}
                              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                                <div className="flex flex-wrap gap-2">
                                  {formData.selectedDelegates.map(delegateId => {
                                    const member = teamMembers.find(m => m.id === delegateId);
                                    return member ? (
                                      <div
                                        key={delegateId}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-white rounded-full text-sm border border-blue-200"
                                      >
                                        <span className="text-gray-700">{member.name}</span>
                                        <button
                                          type="button"
                                          onClick={() => setFormData(prev => ({
                                            ...prev,
                                            selectedDelegates: prev.selectedDelegates.filter(id => id !== delegateId)
                                          }))}
                                          className="text-gray-400 hover:text-red-600 transition-colors"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </div>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-full py-8 text-center bg-gray-50 rounded-xl">
                          <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Loading team members...</p>
                          {!projectId && (
                            <p className="text-xs text-gray-400 mt-1">No project selected</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Additional Notes */}
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-3 block">
                        Additional Notes (Optional)
                      </label>
                      <textarea
                        placeholder="Enter any additional information or requirements..."
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>

                    {/* Create Quote Button */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleCreateQuote}
                      disabled={creatingQuote || !courseSearch.trim()}
                      className="w-full py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {creatingQuote ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Requesting Quote...
                        </>
                      ) : (
                        'Request Quote'
                      )}
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default QuoteModalV2;